import { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, MenuItem, Paper, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  permissionsApi, rolesApi,
  type PermissionDto,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";

/**
 * UC-M1-SEC-02 / admin.role.manage — permission matrix grid.
 * Roles on the column header, permission codes grouped by module on the rows.
 * SUPER_ADMIN column is read-only (server enforces and we visually lock it).
 */
export function PermissionMatrixPage() {
  const qc = useQueryClient();
  const canManage = useAuth(s => s.hasPermission("admin.role.manage"));
  const [filter, setFilter] = useState("");

  const roles = useQuery({ queryKey: ["roles"], queryFn: () => rolesApi.list(), staleTime: 60_000 });
  const permissions = useQuery({ queryKey: ["permissions-all"], queryFn: () => permissionsApi.list(), staleTime: 60_000 });

  // For each role we need the bound permission set. We fetch them in parallel.
  const roleDetails = useQueries({
    queries: (roles.data ?? []).map(r => ({
      queryKey: ["role", r.id],
      queryFn: () => rolesApi.get(r.id),
      staleTime: 30_000,
    })),
  });

  // Local checkbox state — keyed by `${roleId}|${permissionCode}` → boolean.
  // Persisted only on Save per role; reset to server state on role detail invalidation.
  const [pending, setPending] = useState<Record<string, boolean>>({});

  // Build matrix lookup: roleId × permissionCode → boolean.
  const checked = useMemo(() => {
    const map = new Map<string, Set<string>>();
    roleDetails.forEach(rd => {
      if (!rd.data) return;
      map.set(rd.data.id, new Set(rd.data.permissions.map(p => p.code)));
    });
    return map;
  }, [roleDetails.map(rd => rd.data).join("|")]);  // eslint-disable-line react-hooks/exhaustive-deps

  const isOn = (roleId: string, code: string) => {
    const key = `${roleId}|${code}`;
    if (key in pending) return pending[key];
    return checked.get(roleId)?.has(code) ?? false;
  };

  const toggle = (roleId: string, code: string) =>
    setPending(p => ({ ...p, [`${roleId}|${code}`]: !isOn(roleId, code) }));

  const saveRole = useMutation({
    mutationFn: async (roleId: string) => {
      const codes = (permissions.data ?? [])
        .filter(p => isOn(roleId, p.code))
        .map(p => p.code);
      await rolesApi.assignPermissions(roleId, codes);
    },
    onSuccess: (_data, roleId) => {
      qc.invalidateQueries({ queryKey: ["role", roleId] });
      // Drop the pending entries for this role.
      setPending(prev => {
        const next = { ...prev };
        Object.keys(next).forEach(k => { if (k.startsWith(roleId + "|")) delete next[k]; });
        return next;
      });
    },
  });

  // Group permissions by module.
  const grouped = useMemo(() => {
    const byModule = new Map<string, PermissionDto[]>();
    (permissions.data ?? [])
      .filter(p => !filter || p.code.toLowerCase().includes(filter.toLowerCase()))
      .forEach(p => {
        if (!byModule.has(p.module)) byModule.set(p.module, []);
        byModule.get(p.module)!.push(p);
      });
    return Array.from(byModule.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [permissions.data, filter]);

  useEffect(() => {
    // Reset pending when role detail set changes shape (initial load).
    if (roleDetails.every(rd => rd.isSuccess)) {
      // No-op — local state already reflects server set via `checked` map.
    }
  }, [roleDetails.length]); // eslint-disable-line react-hooks/exhaustive-deps

  if (roles.isLoading || permissions.isLoading) return <CircularProgress />;

  const roleColumns = roles.data ?? [];
  const dirtyByRole = (roleId: string) =>
    Object.keys(pending).some(k => k.startsWith(roleId + "|"));

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>Permission matrix</Typography>
        <TextField size="small" placeholder="Filter codes…" sx={{ width: 240 }}
          value={filter} onChange={(e) => setFilter(e.target.value)} />
      </Stack>

      {!canManage && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You can view but not change role assignments — admin.role.manage required.
        </Alert>
      )}
      {saveRole.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(saveRole.error)}</Alert>}

      <Paper sx={{ p: 2 }}>
        <Box sx={{ overflow: "auto" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 260, fontWeight: 700 }}>Permission</TableCell>
                {roleColumns.map(r => (
                  <TableCell key={r.id} align="center" sx={{ minWidth: 130, fontWeight: 700 }}>
                    <Stack alignItems="center" spacing={0.5}>
                      <Typography variant="body2">{r.name}</Typography>
                      {r.code === "SUPER_ADMIN" && <Chip size="small" color="warning" label="Locked" />}
                      {canManage && r.code !== "SUPER_ADMIN" && (
                        <Button
                          size="small"
                          disabled={!dirtyByRole(r.id) || saveRole.isPending}
                          startIcon={<SaveIcon />}
                          onClick={() => saveRole.mutate(r.id)}>
                          Save
                        </Button>
                      )}
                    </Stack>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {grouped.map(([module, perms]) => (
                <>
                  <TableRow key={`group-${module}`} sx={{ bgcolor: "action.hover" }}>
                    <TableCell colSpan={1 + roleColumns.length} sx={{ fontWeight: 700 }}>
                      {module}
                    </TableCell>
                  </TableRow>
                  {perms.map(p => (
                    <TableRow key={p.code}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>{p.code}</Typography>
                        {p.description && (
                          <Typography variant="caption" color="text.secondary">{p.description}</Typography>
                        )}
                      </TableCell>
                      {roleColumns.map(r => (
                        <TableCell key={r.id} align="center">
                          <Checkbox
                            checked={isOn(r.id, p.code)}
                            disabled={!canManage || r.code === "SUPER_ADMIN"}
                            onChange={() => toggle(r.id, p.code)}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </Box>
  );
}

// silence unused — MenuItem reserved for the role-create dialog in S6 polish.
export const _unused = MenuItem;
